// vim: set ts=2 sts=2 sw=2 et:

import { JUDGE_POSITIONS, JudgePosition } from "../judging/resolveJudgeVotes";
import type { DmxConfig } from "./dmxConfig";
import type { PlatformLiveBridgeProviderId } from "./platformLiveBridgeClient";
import { PlatformLiveProfile, PlatformLiveFeatures, defaultPlatformLiveFeatures } from "./platformLiveProfile";
import {
  PLATFORM_LIVE_WS_PATH,
  appendRoomQuery,
  getDefaultPlatformLivePort,
  roomKeyFromParts,
} from "./platformLiveProtocol";

export interface PlatformLiveProfileDraft {
  readonly tournamentId?: string;
  readonly bridgeProviderId?: PlatformLiveBridgeProviderId;
  readonly day: number;
  readonly platformId: number;
  readonly lanHost: string;
  readonly appPort: number;
  readonly livePort: number;
  readonly dmxConfig: DmxConfig;
  readonly dmxConfigPath?: string;
  readonly features: PlatformLiveFeatures;
}

export interface PlatformLiveUrls {
  readonly wsUrl: string;
  readonly liftingUrl: string;
  readonly spectatorUrl: string | null;
  readonly judgesPanelUrl: string | null;
  readonly streamingUrls: {
    readonly athlete: string;
    readonly overlay: string;
  } | null;
  readonly judgeUrls: Partial<Record<JudgePosition, string>>;
}

const shellQuote = (value: string): string => {
  if (/^[A-Za-z0-9_./:=+-]+$/.test(value)) {
    return value;
  }

  return `'${value.replace(/'/g, "'\\''")}'`;
};

const appendQuery = (url: string, params: Record<string, string>): string => {
  const search = new URLSearchParams(params);
  return `${url}?${search.toString()}`;
};

export type OverlayBackground = "blanco" | "transparente";

/** El overlay es blanco por defecto; con fondo transparente sirve como fuente de navegador de OBS con alfa. */
export const withOverlayBackground = (overlayUrl: string, background: OverlayBackground): string => {
  if (background === "blanco") return overlayUrl;
  return `${overlayUrl}${overlayUrl.includes("?") ? "&" : "?"}fondo=transparente`;
};

export const makeDefaultDmxConfig = (enabled = false): DmxConfig => ({
  enabled,
  driver: "artnet",
  universe: 1,
  target: { type: "unicast", host: "2.0.0.100", port: 6454 },
  fixtures: [
    { name: "left", profile: "dimmer-rgb", startChannel: 1 },
    { name: "center", profile: "dimmer-rgb", startChannel: 5 },
    { name: "right", profile: "dimmer-rgb", startChannel: 9 },
  ],
});

export const makePlatformLiveProfileDraft = (
  values: Partial<PlatformLiveProfileDraft> &
    Pick<PlatformLiveProfileDraft, "day" | "platformId" | "lanHost" | "appPort">,
): PlatformLiveProfileDraft => {
  const features = { ...defaultPlatformLiveFeatures(), ...(values.features || {}) };
  const dmxConfig = values.dmxConfig || makeDefaultDmxConfig(features.dmx);
  return {
    tournamentId: values.tournamentId,
    bridgeProviderId: values.bridgeProviderId,
    day: values.day,
    platformId: values.platformId,
    lanHost: values.lanHost,
    appPort: values.appPort,
    livePort: values.livePort || getDefaultPlatformLivePort(values.platformId),
    dmxConfig: {
      ...dmxConfig,
      enabled: features.dmx,
    },
    dmxConfigPath: values.dmxConfigPath,
    features,
  };
};

export const makePlatformLiveProfile = (draft: PlatformLiveProfileDraft, activatedAt: string): PlatformLiveProfile => ({
  ...draft,
  activatedAt,
});

export const makePlatformLiveRuntimeKey = (profile: PlatformLiveProfile): string => {
  return JSON.stringify({
    tournamentId: profile.tournamentId ?? null,
    bridgeProviderId: profile.bridgeProviderId ?? null,
    day: profile.day,
    platformId: profile.platformId,
    lanHost: profile.lanHost,
    appPort: profile.appPort,
    livePort: profile.livePort,
    dmxConfig: profile.dmxConfig,
    dmxConfigPath: profile.dmxConfigPath,
    features: profile.features,
  });
};

/** Identidad de sala (torneo, día, tarima) — para el Map de runtimes del bridge y el routing WS. */
export const roomKeyOf = (profile: PlatformLiveProfile): string => {
  return roomKeyFromParts(profile.tournamentId, profile.day, profile.platformId);
};

const appendStructuredDmxArgv = (args: Array<string>, config: DmxConfig) => {
  args.push("--dmx");
  args.push("--dmx-driver", config.driver);
  args.push("--dmx-universe", String(config.universe));

  if (config.target.type === "broadcast") {
    args.push("--dmx-target", "broadcast");
  } else {
    args.push("--dmx-target", "host");
    args.push("--dmx-target-host", config.target.host);
  }

  config.fixtures.forEach((fixture) => {
    const profileSuffix = fixture.profile === "rgb" ? "" : `:${fixture.profile}`;
    args.push("--dmx-fixture", `${fixture.name}=${fixture.startChannel}${profileSuffix}`);
  });
};

/**
 * Local command for the cloud transport: the room comes from the same WSS URL
 * published to the Platform clients, while Art-Net settings stay on the venue PC.
 */
export const buildPlatformDmxBridgeArgv = (wsUrl: string, config: DmxConfig): Array<string> => {
  const args = ["--ws", wsUrl];
  appendStructuredDmxArgv(args, config);
  return args;
};

export const buildPlatformDmxBridgeCommand = (wsUrl: string, config: DmxConfig): string => {
  return ["yarn wrpf:dmx-bridge", ...buildPlatformDmxBridgeArgv(wsUrl, config).map(shellQuote)].join(" ");
};

export const buildPlatformLiveArgv = (profile: PlatformLiveProfile): Array<string> => {
  const args = [
    "--day",
    String(profile.day),
    "--platform",
    String(profile.platformId),
    "--port",
    String(profile.livePort),
    "--host-ip",
    profile.lanHost,
    "--app-host",
    profile.lanHost,
    "--app-port",
    String(profile.appPort),
  ];

  if (!profile.features.judgeVoting) {
    args.push("--no-judge-voting");
  }
  if (!profile.features.spectatorDisplay) {
    args.push("--no-spectator-display");
  }
  if (profile.features.streamingDisplays) {
    args.push("--streaming-displays");
  }
  if (profile.features.manualVerdictConfirmation === true) {
    args.push("--manual-verdict-confirmation");
  }
  if (profile.features.dmx) {
    if (profile.dmxConfig !== undefined) {
      appendStructuredDmxArgv(args, profile.dmxConfig);
    } else if (profile.dmxConfigPath !== undefined) {
      args.push("--dmx-config", profile.dmxConfigPath);
    } else {
      appendStructuredDmxArgv(args, makeDefaultDmxConfig(true));
    }
  }

  return args;
};

export const buildPlatformLiveCommand = (profile: PlatformLiveProfile): string => {
  return ["yarn wrpf:live", ...buildPlatformLiveArgv(profile).map(shellQuote)].join(" ");
};

export const buildPlatformLiveUrls = (profile: PlatformLiveProfile): PlatformLiveUrls => {
  // Local mode: each platform has its own port, so the port already disambiguates the room — no
  // room query needed. (The cloud unified host shares one port and DOES need it; see below.)
  const wsUrl = `ws://${profile.lanHost}:${profile.livePort}${PLATFORM_LIVE_WS_PATH}`;
  const appBase = `http://${profile.lanHost}:${profile.appPort}`;
  return buildPlatformLiveUrlsWithEndpoints(profile, wsUrl, appBase);
};

export const bridgeUrlToWebSocketUrl = (bridgeUrl: string): string => {
  const url = new URL(bridgeUrl);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = PLATFORM_LIVE_WS_PATH;
  url.search = "";
  url.hash = "";
  return url.toString();
};

export const buildPlatformLiveUrlsForRemoteBridge = (
  profile: PlatformLiveProfile,
  bridgeUrl: string,
  appOrigin: string,
): PlatformLiveUrls => {
  const wsUrl = appendRoomQuery(bridgeUrlToWebSocketUrl(bridgeUrl), profile);
  const appBase = appOrigin.replace(/\/+$/, "");
  return buildPlatformLiveUrlsWithEndpoints(profile, wsUrl, appBase);
};

const buildPlatformLiveUrlsWithEndpoints = (
  profile: PlatformLiveProfile,
  wsUrl: string,
  appBase: string,
): PlatformLiveUrls => {
  const platformBase = `${appBase}/days/${profile.day}/platforms/${profile.platformId}`;
  const liftingUrl = appendQuery(`${appBase}/`, { lifting: "", ws: wsUrl }).replace("?lifting=&", "?lifting&");
  const judgeUrls: Partial<Record<JudgePosition, string>> = {};

  if (profile.features.judgeVoting) {
    JUDGE_POSITIONS.forEach((position) => {
      judgeUrls[position] = appendQuery(`${platformBase}/judges/${position}`, {
        ws: wsUrl,
      });
    });
  }

  return {
    wsUrl,
    liftingUrl,
    spectatorUrl: profile.features.spectatorDisplay ? appendQuery(`${platformBase}/spectator`, { ws: wsUrl }) : null,
    judgesPanelUrl: profile.features.judgeVoting ? appendQuery(`${platformBase}/judges`, { ws: wsUrl }) : null,
    streamingUrls: profile.features.streamingDisplays
      ? {
          athlete: appendQuery(`${platformBase}/streaming/athlete`, { ws: wsUrl }),
          overlay: appendQuery(`${platformBase}/streaming/overlay`, { ws: wsUrl }),
        }
      : null,
    judgeUrls,
  };
};
